import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import logo from '../Images/ringpelogocugpws.png';

const menuOptions = [
  { id: 1, name: 'HOME', path: '/', sectionId: 'home' },
  { id: 2, name: 'FEATURES', path: '/#features', sectionId: 'features' },
  { id: 3, name: 'ADVANTAGES', path: '/#advantages', sectionId: 'advantages' },
  { id: 4, name: 'FAQ', path: '/#faq', sectionId: 'faq' },
  { id: 5, name: 'SERVICES', path: '/#bbpsPoints', sectionId: 'bbpsPoints' },
  { id: 6, name: 'CONTACT', path: '/#footer', sectionId: 'footer' },
];

const Header1 = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState('/');
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    // Extract the hash from the location
    const hash = location.hash;
    const path = hash ? `/${hash}` : location.pathname;
    setSelected(path);
    setIsMenuOpen(false); // Close menu on navigation
    
    // Scroll to the section if there's a hash
    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else if (location.pathname === '/') {
      // Scroll to top for home
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  useEffect(() => {
    // Add scroll event listener to track which section is in view
    const handleScroll = () => {
      // Get all sections that correspond to menu items
      const sections = menuOptions
        .map(option => {
          const sectionId = option.sectionId || option.path.replace('/#', '');
          return document.getElementById(sectionId);
        })
        .filter(Boolean);

      // Find the section that is currently in view
      let currentSection = '';
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        
        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          currentSection = section.id;
        }
      });

      // Special case for home when at the top of the page
      if (scrollPosition < window.innerHeight / 2 && sections[0]?.id === 'home') {
        currentSection = 'home';
      }

      if (currentSection && currentSection !== activeSection) {
        setActiveSection(currentSection);
        // Update the selected menu item based on the active section
        const matchingOption = menuOptions.find(option => 
          option.sectionId === currentSection || option.path.includes(currentSection)
        );
        if (matchingOption) {
          setSelected(matchingOption.path);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeSection]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close menu only if clicking outside both menu and toggle button
      if (
        isMenuOpen && 
        menuRef.current && 
        !menuRef.current.contains(event.target) &&
        toggleRef.current && 
        !toggleRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const handleMenuClick = (path) => {
    setSelected(path);
    navigate(path);
  };

  return (
    <header className="header" style={{ width: '100%' }}>
      <div className="header-container">
        <div className="logo">
          <img src={logo} alt="Ringpe Logo" />
        </div>
        <nav ref={menuRef} className={`menu ${isMenuOpen ? 'open' : ''}`}>
          {menuOptions.map((option) => (
            <button
              key={option.id}
              className={`menu-link${selected === option.path ? ' selected' : ''}`}
              onClick={() => handleMenuClick(option.path)}
              tabIndex={0}
            >
              {option.name}
              <span className="menu-underline" />
            </button>
          ))}
        </nav>
        <button
          ref={toggleRef}
          className="menu-toggle"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </button>
      </div>
    </header>
  );
};

export default Header1;